import { useEffect, useState } from "react";
import { useOwner } from "./OwnerContext";
import { useContent } from "../content/ContentContext";
import { SectionLabel, Logo } from "../components/Ui";
import { setPageMeta } from "../lib/seo";
import Inbox from "./Inbox";
import Invoice from "./Invoice";
import Templates from "./Templates";
import OwnerEditor from "./OwnerEditor";

const inputCls =
  "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink transition-colors focus:border-accent";

function AuthCard({ mode }) {
  const { setup, login } = useOwner();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (mode === "setup") {
      if (pw.length < 10) { setError("Password must be at least 10 characters."); return; }
      if (pw !== confirm) { setError("Passwords do not match."); return; }
      setup(pw, setError, setLoading);
    } else {
      login(pw, setError, setLoading);
    }
  };

  return (
    <main className="mx-auto flex max-w-md flex-col items-center px-4 py-20">
      <Logo />
      <div className="mt-10 w-full rounded-2xl border border-line bg-white p-8 sm:p-10">
        <SectionLabel>{mode === "setup" ? "First-time setup" : "Owner sign-in"}</SectionLabel>
        <h1 className="mt-4 font-serif text-2xl font-medium text-ink">
          {mode === "setup" ? "Create your owner password." : "Enter your owner password."}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-muted">
          {mode === "setup"
            ? "This password protects your enquiry inbox, the no-code editor, invoice builder and document templates. It is stored only as a server-side hash — never in the website code. Make a note of it somewhere safe."
            : "Your enquiry inbox, editor, invoice builder and document templates are protected."}
        </p>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="owner-auth-form">
          <div>
            <label htmlFor="owner-password" className="mb-2 block text-sm font-medium text-ink">Password</label>
            <input id="owner-password" data-testid="owner-password-input" type="password" required
              minLength={mode === "setup" ? 10 : 1} value={pw} onChange={(e) => setPw(e.target.value)}
              className={inputCls} autoComplete={mode === "setup" ? "new-password" : "current-password"} />
          </div>
          {mode === "setup" && (
            <div>
              <label htmlFor="owner-password-confirm" className="mb-2 block text-sm font-medium text-ink">Confirm password</label>
              <input id="owner-password-confirm" data-testid="owner-password-confirm-input" type="password" required
                value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} autoComplete="new-password" />
            </div>
          )}
          {error && (
            <p role="alert" data-testid="owner-auth-error" className="rounded-xl border border-accent bg-lavender px-4 py-3 text-sm text-plum">
              {error}
            </p>
          )}
          <button data-testid="owner-auth-submit" type="submit" disabled={loading}
            className="w-full rounded-full bg-plum px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-ink disabled:opacity-60">
            {loading ? "Please wait…" : mode === "setup" ? "Create password & sign in" : "Sign in"}
          </button>
        </form>
      </div>
    </main>
  );
}

function OwnerNotes() {
  return (
    <div className="space-y-8" data-testid="owner-notes">
      <div className="rounded-2xl border border-line bg-white p-8">
        <h2 className="font-serif text-xl font-medium text-ink">What is configured</h2>
        <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted">
          <li>— Enquiries from the public contact form are stored securely in the site database and appear in your Inbox tab. No email notifications are configured (add them only if a free tier is available — nothing paid is active).</li>
          <li>— This owner area is protected by server-side authentication (bcrypt-hashed password, signed session cookie with server-side revocation). No password is stored in the website code.</li>
          <li>— All public content is stored in the site database with code defaults as backup, so every edit persists after refreshing.</li>
          <li>— The invoice builder and document templates print to PDF from your browser (Print → “Save as PDF”).</li>
        </ul>
      </div>
      <div className="rounded-2xl border border-line bg-white p-8">
        <h2 className="font-serif text-xl font-medium text-ink">How to edit without coding</h2>
        <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted">
          <li>— <span className="font-semibold text-ink">Change a price:</span> Editor → Services (or Packages) → change “Starting price (€)” → Save changes. The public page updates immediately.</li>
          <li>— <span className="font-semibold text-ink">Replace an image:</span> Editor → Images → choose a file, crop it, then press “Set as About portrait” (or paste its URL into the About portrait field on the Site text tab).</li>
          <li>— <span className="font-semibold text-ink">Edit a case study:</span> Editor → Case studies → open a study → edit narrative, achieved results, findings → Save.</li>
          <li>— <span className="font-semibold text-ink">Update dashboard data:</span> Editor → Dashboard data → pick a dataset, edit cells or import CSV → Save. The interactive dashboards recalculate from the new data.</li>
          <li>— <span className="font-semibold text-ink">Edit process maps:</span> Editor → Process map → change step labels, descriptions, responsibilities and flags.</li>
          <li>— <span className="font-semibold text-ink">Text and contact details:</span> Editor → Site text &amp; headings.</li>
        </ul>
      </div>
      <div className="rounded-2xl border border-line bg-lavender p-8">
        <h2 className="font-serif text-xl font-medium text-ink">Needs your review</h2>
        <ul className="mt-4 space-y-2 text-sm leading-relaxed text-plum">
          <li>— Euro prices are provisional launch proposals for your review — adjust them in the editor.</li>
          <li>— Invoice: add your business registration details, tax status and payment details before issuing. The invoice is a draft design, not a compliant French e-invoicing system.</li>
          <li>— Privacy notice: confirm the data retention period and final hosting arrangement (items marked [To confirm]).</li>
          <li>— Hosting: the site currently runs on the platform&rsquo;s preview subdomain. Verify publishing options on your plan before launch — no domain has been purchased.</li>
          <li>— Case study narratives: check the anonymised descriptions and achieved results match what you can confirm.</li>
        </ul>
      </div>
    </div>
  );
}

function Dashboard() {
  const { logout } = useOwner();
  const { content } = useContent();
  const [tab, setTab] = useState("inbox");
  const tabs = [
    { id: "inbox", label: "Enquiry inbox" },
    { id: "editor", label: "Editor" },
    { id: "invoice", label: "Invoice" },
    { id: "templates", label: "Documents" },
    { id: "notes", label: "Owner notes" },
  ];
  return (
    <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8" data-testid="owner-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <SectionLabel>Private owner area</SectionLabel>
          <h1 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-ink">Your workspace.</h1>
        </div>
        <button data-testid="owner-logout-button" onClick={logout}
          className="rounded-full border border-line px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-plum hover:text-plum">
          Sign out
        </button>
      </div>
      <div className="mt-8 flex flex-wrap gap-2 border-b border-line pb-4" role="tablist" aria-label="Owner area sections">
        {tabs.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} data-testid={`owner-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === t.id ? "bg-plum text-white" : "border border-line bg-white text-ink hover:border-plum hover:text-plum"
            }`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="mt-10">
        {tab === "inbox" && <Inbox />}
        {tab === "editor" && (
          <OwnerEditor
            site={content.site}
            services={content.services}
            packages={content.packages}
            caseStudies={content.caseStudies}
            dashboards={content.dashboards}
            process={content.process}
          />
        )}
        {tab === "invoice" && <Invoice />}
        {tab === "templates" && <Templates />}
        {tab === "notes" && <OwnerNotes />}
      </div>
    </main>
  );
}

export default function Owner() {
  const { phase } = useOwner();
  useEffect(() => {
    setPageMeta("Owner area — Savvy Stephanie", "Private owner workspace.");
  }, []);
  if (phase === "checking") {
    return (
      <main className="mx-auto max-w-md px-4 py-24 text-center">
        <p className="font-mono text-sm text-muted" data-testid="owner-checking">Checking your session…</p>
      </main>
    );
  }
  if (phase === "setup") return <AuthCard mode="setup" />;
  if (phase === "login") return <AuthCard mode="login" />;
  return <Dashboard />;
}
